/**
 * Group dance (รำกลุ่ม, 群舞): five dancers in a ring take turns pressing a sequence of arrows to
 * the beat, ten rounds. Dancing in harmony speeds cultivation while it lasts and for a while
 * afterwards, and pays training points at the end. The rules are in groupDance.ts; this is the
 * tuning. Every time here is in seconds of world time (World.step), never the client's clock.
 * Design and the reasoning behind the numbers: docs/systems/GROUP_DANCE.md.
 */

export type DanceDir = 'up' | 'down' | 'left' | 'right';
export const DANCE_DIRS: readonly DanceDir[] = ['up', 'down', 'left', 'right'];

export type DanceGrade = 'perfect' | 'good' | 'miss';

/** One round: how many arrows each dancer presses, and the beat between two of them. */
export interface DanceRound {
  readonly arrows: number;
  readonly beat: number;
}

/** Ten rounds, longer and quicker as they go. */
export const DANCE_ROUNDS: readonly DanceRound[] = [
  { arrows: 3, beat: 0.6 },
  { arrows: 3, beat: 0.6 },
  { arrows: 4, beat: 0.55 },
  { arrows: 4, beat: 0.55 },
  { arrows: 5, beat: 0.5 },
  { arrows: 5, beat: 0.5 },
  { arrows: 6, beat: 0.46 },
  { arrows: 6, beat: 0.46 },
  { arrows: 7, beat: 0.42 },
  { arrows: 8, beat: 0.4 },
];

export const GROUP_DANCE = {
  /** Dancers in the ring, the leader included (empty places are filled by hired dancers). */
  size: 5,
  rounds: DANCE_ROUNDS.length,
  /** Ring radius (tiles), and how far a dancer may stray from their place before leaving it. */
  ringRadius: 1.5,
  leaveDistance: 1.6,
  /** Party members this close to the leader take their place in the ring. */
  joinRange: 12,

  // ── timing ──
  /** Seconds the ring takes to form before the first turn. */
  gatherSeconds: 3,
  /** From a turn's start (its arrows shown) to its first arrow. */
  leadIn: 1.2,
  /** Rest after a turn's last arrow, and after a round. */
  turnGap: 0.35,
  roundPause: 1.5,
  /** The ring dances on after the last round, then breaks up. */
  finaleSeconds: 3,
  /** Within this of an arrow's beat: perfect; within `good`: good; later than `good`: a miss. */
  perfect: 0.08,
  good: 0.18,
  /** Pressed earlier than this before the beat: not counted at all (no mashing ahead). */
  early: 0.35,
  /**
   * Inputs carry the world time they were pressed at; the server accepts one this old at most
   * (network lag), and only calls an arrow missed once this much more time has passed.
   */
  maxLag: 0.15,

  // ── scoring ──
  points: { perfect: 100, good: 50, miss: 0 } as Readonly<Record<DanceGrade, number>>,
  /** Accuracy weight of a grade (perfect counts whole, good a little over half). */
  accuracy: { perfect: 1, good: 0.6, miss: 0 } as Readonly<Record<DanceGrade, number>>,
  /** Points grow 1% per arrow of unbroken group combo, up to this many. */
  comboCap: 50,
  /** Harmony (ความกลมเกลียว, 0–100): where it starts, and how each arrow moves it. */
  harmonyStart: 50,
  harmony: { perfect: 2, good: 1, miss: -5 } as Readonly<Record<DanceGrade, number>>,
  /** A turn without a miss lifts harmony this much more. */
  cleanTurn: 3,

  // ── AI dancers ──
  /** Chance a hired dancer hits an arrow; party fellows go by the player's power rank. */
  hiredHit: 0.85,
  fellowHit: { base: 0.72, perRank: 0.006, max: 0.95 },
  /** Of the arrows an AI dancer hits, the share that are perfect. */
  aiPerfectShare: 0.7,

  // ── rewards ──
  /**
   * While the ring dances, cultivation builds at the meditation rate × (1 + harmony / 100 × this):
   * from ×1 to ×2 of meditating, by how well the ring keeps together.
   */
  duringBonus: 1,
  /** Training points (แต้มฝึกปรือ) at the end: base + perf × perPerf, before the party bonus. */
  rewardBase: 300,
  rewardPerf: 1200,
  /** Each party member dancing beside the player adds this to the end reward. */
  partyBonus: 0.05,
  /** Afterwards (พลังประสาน): cultivation and neigong training × (1 + perf × buffPerf) for buffSeconds. */
  buffPerf: 0.5,
  buffSeconds: 1200,
  /** Sessions a day that pay; more may be danced for fun. */
  dailyRuns: 3,
  /** A ring broken off early pays for the rounds danced, if at least this many were finished. */
  partialMinRounds: 3,
} as const;

/** Grades of a whole session, best first, by the least performance (0–1) each needs. */
export const DANCE_RANKS: readonly { readonly grade: string; readonly min: number; readonly name: string }[] =
  [
    { grade: 'S', min: 0.9, name: 'สอดประสานดั่งหนึ่งเดียว' },
    { grade: 'A', min: 0.75, name: 'กลมเกลียว' },
    { grade: 'B', min: 0.55, name: 'พร้อมเพรียง' },
    { grade: 'C', min: 0.35, name: 'พอไปได้' },
    { grade: 'D', min: 0, name: 'ต่างคนต่างรำ' },
  ];

/**
 * Dances (emote ids, data/emotes) the ring plays: the dancer whose turn it is takes a lively one
 * and changes pose on every arrow hit; the others sway to a calm one. Any not in the emote data
 * falls back to the plain `dance` emote.
 */
export const DANCE_LIVELY: readonly string[] = [
  'danceJig',
  'danceLion',
  'danceStomp',
  'danceTwirl',
  'danceShuffle',
];
export const DANCE_CALM: readonly string[] = ['danceSleeve', 'danceFan', 'danceTaichi', 'danceSword'];
export const DANCE_FALLBACK = 'dance';

/** The troupe that fills a ring short of five (they come and go with the dance). */
export interface HiredDancerDef {
  readonly name: string;
  readonly sprite: string;
}
export const HIRED_DANCERS: readonly HiredDancerDef[] = [
  { name: 'เหมยเหมย', sprite: 'heroJileguF' },
  { name: 'ลี่ฮวา', sprite: 'heroWudangF' },
  { name: 'ซิ่วหลาน', sprite: 'heroJunzitangF' },
  { name: 'อวี้เอ๋อร์', sprite: 'heroShaolinF' },
];
export const HIRED_TITLE = 'คณะนางรำเมฆา';

/** Why a ring broke up. */
export type DanceEndReason = 'done' | 'left' | 'attacked' | 'fallen' | 'alone';

export const DANCE_END_TEXT: Readonly<Record<DanceEndReason, string>> = {
  done: 'รำกลุ่มครบสิบรอบ',
  left: 'ออกจากวงรำ',
  attacked: 'ถูกโจมตี วงรำแตก',
  fallen: 'หมดสติ วงรำแตก',
  alone: 'ไม่มีผู้เล่นเหลือในวง',
};
